import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const PACK = 'MEGA KIT ELECTRICISTA PRO™';
type Action = 'buy' | 'cart';
interface Purchase {
  name: string;
  location: string;
  action: Action;
  time: string;
}
// 20 rotativos con nombres, ciudades y países variados de LATAM.
// Solo dos acciones permitidas: "compró" (buy) y "añadió al carrito" (cart).
const purchases: Purchase[] = [{
  name: 'Juan P.',
  location: 'Guadalajara, México',
  action: 'buy',
  time: 'hace 4 min'
}, {
  name: 'Carlos M.',
  location: 'Bogotá, Colombia',
  action: 'cart',
  time: 'hace 6 min'
}, {
  name: 'Luis R.',
  location: 'Lima, Perú',
  action: 'buy',
  time: 'hace 8 min'
}, {
  name: 'Andrés G.',
  location: 'Santiago, Chile',
  action: 'cart',
  time: 'hace 5 min'
}, {
  name: 'Miguel A.',
  location: 'Medellín, Colombia',
  action: 'buy',
  time: 'hace 7 min'
}, {
  name: 'Roberto S.',
  location: 'Quito, Ecuador',
  action: 'cart',
  time: 'hace 11 min'
}, {
  name: 'José L.',
  location: 'La Paz, Bolivia',
  action: 'buy',
  time: 'hace 9 min'
}, {
  name: 'Martín C.',
  location: 'Montevideo, Uruguay',
  action: 'cart',
  time: 'hace 13 min'
}, {
  name: 'Daniel F.',
  location: 'Asunción, Paraguay',
  action: 'buy',
  time: 'hace 10 min'
}, {
  name: 'Pedro V.',
  location: 'San José, Costa Rica',
  action: 'cart',
  time: 'hace 14 min'
}, {
  name: 'Héctor N.',
  location: 'Ciudad de Panamá, Panamá',
  action: 'buy',
  time: 'hace 12 min'
}, {
  name: 'Ricardo T.',
  location: 'Santo Domingo, República Dominicana',
  action: 'cart',
  time: 'hace 15 min'
}, {
  name: 'Fernando R.',
  location: 'Córdoba, Argentina',
  action: 'buy',
  time: 'hace 16 min'
}, {
  name: 'Jorge M.',
  location: 'Puebla, México',
  action: 'cart',
  time: 'hace 18 min'
}, {
  name: 'Esteban C.',
  location: 'Cali, Colombia',
  action: 'buy',
  time: 'hace 19 min'
}, {
  name: 'Víctor H.',
  location: 'Arequipa, Perú',
  action: 'cart',
  time: 'hace 21 min'
}, {
  name: 'Samuel D.',
  location: 'Santa Cruz, Bolivia',
  action: 'buy',
  time: 'hace 17 min'
}, {
  name: 'Mauricio L.',
  location: 'Valparaíso, Chile',
  action: 'cart',
  time: 'hace 20 min'
}, {
  name: 'Diego P.',
  location: 'Guayaquil, Ecuador',
  action: 'buy',
  time: 'hace 22 min'
}, {
  name: 'Alejandro B.',
  location: 'Rosario, Argentina',
  action: 'cart',
  time: 'hace 24 min'
}];
// Rangos de tiempo (ms) sugeridos para una sensación natural, no saturada.
const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);
export function PurchaseToast() {
  const [current, setCurrent] = useState<Purchase | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastIndexRef = useRef<number>(-1);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) setHasScrolled(true);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!hasScrolled) return;
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const pickNextIndex = () => {
      // Índice aleatorio que evita repetir el inmediatamente anterior.
      if (purchases.length <= 1) return 0;
      let next = Math.floor(Math.random() * purchases.length);
      while (next === lastIndexRef.current) {
        next = Math.floor(Math.random() * purchases.length);
      }
      lastIndexRef.current = next;
      return next;
    };
    const cycle = () => {
      setCurrent(purchases[pickNextIndex()]);
      setIsVisible(true);
      // Visible entre 4 y 6 segundos.
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        // Próximo pop-up entre 12 y 18 segundos.
        showTimer = setTimeout(cycle, randomBetween(12000, 18000));
      }, randomBetween(4000, 6000));
    };
    // Primer pop-up tras un breve margen.
    showTimer = setTimeout(cycle, 2500);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [hasScrolled]);
  return <AnimatePresence data-id="element-261">
      {isVisible && current && <motion.div initial={{
      opacity: 0,
      y: -20,
      x: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -20,
      x: 20,
      scale: 0.95
    }} transition={{
      duration: 0.35,
      ease: 'easeOut'
    }} className="fixed top-4 right-4 left-4 sm:left-auto z-[100] ml-auto w-auto max-w-xs bg-white border-l-4 border-electric p-3 md:p-4 rounded shadow-xl flex items-center gap-3 md:gap-4" data-id="element-262">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-navy rounded flex items-center justify-center text-electric font-black text-xs md:text-base shrink-0" data-id="element-263">
            {current.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0" data-id="element-264">
            <div className="flex items-start justify-between gap-2" data-id="element-265">
              <p className="text-[11px] md:text-sm font-bold text-navy leading-tight" data-id="element-266">
                {current.name} de {current.location}
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full ml-2 animate-pulse align-middle" data-id="element-267"></span>
              </p>
              <button onClick={() => setIsVisible(false)} aria-label="Cerrar notificación" className="text-slate-400 hover:text-navy bg-slate-100 rounded p-1 shrink-0" data-id="element-268">
                <X size={12} data-id="element-269" />
              </button>
            </div>
            <p className="text-xs text-slate-600 font-medium mt-0.5 leading-tight" data-id="element-270">
              {current.action === 'cart' ? 'añadió al carrito el ' : 'compró el '}
              <span className="text-navy font-bold" data-id="element-271">{PACK}</span>
            </p>
            <p className="text-[10px] text-slate-400 mt-1 font-semibold" data-id="element-272">
              {current.time}
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}